import React, { useEffect } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import Button from "web/react/components/button/button";
import CookieSettingsContainer from "web/react/components/cookie-settings-container/cookie-settings-container";
import GenericModal from "web/react/components/generic-modal/generic-modal";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import { consentToAllCookies } from "web/redux/ducks/cookie-consent";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { getUrl } from "web/script/routing/getUrl";
import history from "web/script/utils/history";
import styles from "./cookie-consent-modal.module.css";
import cookieIcon from "./images/cookie-icon.png";

export default function CookieConsentModal(): JSX.Element {
    const dispatch = useDispatch();
    const consentConfirmed = useReduxSelector((state: ReduxStoreState) => {
        return state.cookieConsentReducer.consentConfirmed;
    });
    const cookieSettingsModal = "cookie-settings-banner-modal";
    const settingsModalIsOpen = Boolean(useOverlayWatcher(cookieSettingsModal));
    // we want to allow the user to browse the cookie policy page freely
    const showModal =
        !consentConfirmed &&
        !settingsModalIsOpen &&
        environment.get("pageSubType") !== "cookie_policy";

    function onSettingsModalClose(): void {
        history.popOverlay(cookieSettingsModal);
    }

    function onSettingsModalOpen(): void {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay != cookieSettingsModal) {
            history.pushOverlay(cookieSettingsModal);
        }
    }

    function onAcceptButtonClick(): void {
        dispatch(consentToAllCookies());
        analytics.event("Cookie consent", "Clicked", "Hide");
    }

    useEffect(() => {
        if (showModal) {
            analytics.event("Cookie consent", "Shown", undefined, true);
        }
    }, [showModal]);

    return (
        <>
            <GenericModal
                className={styles.modal}
                isOpen={showModal}
                onClose={(): void => {}}
                noScroll={false}
                closable={false}
            >
                <div className={styles.cookieConsentModalContainer}>
                    <img src={cookieIcon} className={styles.cookieIcon} alt={"Cookies"} />
                    <div className={styles.contentContainer}>
                        <span className={styles.title}>
                            {gettext("cookie_notification.modal.title")}
                        </span>
                        <div className={styles.details}>
                            <p>{`${gettext("cookie_notification.description_v2_1")} `}</p>
                            <p>
                                <span>{`${gettext("cookie_notification.description_v2_2")} `}</span>
                                <span>
                                    <a
                                        className={styles.readMore}
                                        href={getUrl("helpCookiePolicy")}
                                    >
                                        {gettext("general.read_more")}
                                    </a>
                                </span>
                            </p>
                        </div>
                        <div className={styles.buttonsContainer}>
                            <Button
                                styleType={"cta"}
                                fullWidth
                                className={styles.acceptButton}
                                onClick={onAcceptButtonClick}
                            >
                                {gettext("general.accept")}
                            </Button>
                            <Button
                                styleType={"tertiary"}
                                fullWidth
                                className={styles.customizeButton}
                                onClick={onSettingsModalOpen}
                            >
                                {gettext("cookie_notification.modal.customize_cta")}
                            </Button>
                        </div>
                    </div>
                </div>
            </GenericModal>
            <CookieSettingsContainer
                isOpen={settingsModalIsOpen}
                onModalClose={onSettingsModalClose}
            />
        </>
    );
}
